import React, { useState, useEffect } from 'react';
import { 
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Dialog from '../common/Dialog';
import { User, Role } from '../../services/users';
import './EditUserModal.css';

export interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (userData: Partial<User>) => Promise<void>;
  user: User | null;
  roles: Role[];
  loading?: boolean;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  onSave,
  user,
  loading = false
}) => {
  const [formData, setFormData] = useState<Partial<User>>({
    username: '',
    email: '',
    name: '',
    phone: '',
    locale: 'fr',
    is_blocked: false,
    must_change_password: false,
    twofa_enabled: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialiser le formulaire quand l'utilisateur change
  useEffect(() => {
    if (user && isOpen) {
      // Déterminer prénom/nom à partir des champs dédiés ou du nom complet
      const derivedFirstName = user.first_name && user.first_name.trim()
        ? user.first_name
        : (user.name ? user.name.split(' ')[0] : '');
      const derivedLastName = user.last_name && user.last_name.trim()
        ? user.last_name
        : (user.name ? user.name.split(' ').slice(1).join(' ') : '');

      setFormData({
        username: user.username,
        email: user.email,
        name: user.name,
        first_name: derivedFirstName,
        last_name: derivedLastName,
        phone: user.phone,
        locale: user.locale,
        is_blocked: user.is_blocked,
        must_change_password: user.must_change_password,
        twofa_enabled: user.twofa_enabled,
        is_active: user.is_active
      });
      setErrors({});
    }
  }, [user, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validation du nom d'utilisateur
    if (!formData.username?.trim()) {
      newErrors.username = 'Le nom d\'utilisateur est requis';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
    }

    // Validation de l'email
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    // Validation du nom complet
    if (!formData.name?.trim()) {
      newErrors.name = 'Le nom complet est requis';
    }

    // Validation du téléphone
    if (formData.phone && !/^[\+]?[0-9\s\-\(\)]{8,}$/.test(formData.phone)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof User, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Préparer les données acceptées et nécessaires par le backend
      // - Inclure username pour éviter les erreurs quand le backend attend ce champ
      // - Ne pas envoyer les champs undefined
      const rawUpdateData: Record<string, any> = {
        username: formData.username,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        is_active: formData.is_active
      };
      const updateData = Object.fromEntries(
        Object.entries(rawUpdateData).filter(([_, v]) => v !== undefined)
      );
      
      await onSave(updateData);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Extraire le prénom et nom du nom complet
    const nameParts = user?.name ? user.name.split(' ') : ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    setFormData({
      username: user?.username || '',
      email: user?.email || '',
      first_name: firstName,
      last_name: lastName,
      phone: user?.phone || '',
      locale: user?.locale || 'fr',
      is_blocked: user?.is_blocked || false,
      must_change_password: user?.must_change_password || false,
      twofa_enabled: user?.twofa_enabled || false
    });
    setErrors({});
    onClose();
  };
  
  if (!user) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title="Modifier l'utilisateur"
      size="lg"
      className="edit-user-modal"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations de base */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prénom *
            </label>
            <input
              type="text"
              value={formData.first_name || ''}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.first_name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Prénom"
              disabled={loading}
            />
            {errors.first_name && (
              <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom *
            </label>
            <input
              type="text"
              value={formData.last_name || ''}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.last_name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom"
              disabled={loading}
            />
            {errors.last_name && (
              <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
            )}
          </div>
          <div className='md:col-span-2'>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom d'utilisateur *
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={formData.username || ''}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.username ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Nom d'utilisateur"
                disabled={loading}
              />
            </div>
            {errors.username && (
              <p className="mt-1 text-sm text-red-600">{errors.username}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="email@exemple.com"
                disabled={loading}
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <div className="relative">
              <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="+22501234567"
                disabled={loading}
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>
        </div>
             {/* Aperçu du nom complet */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Aperçu du nom complet :</h4>
          <p className="text-lg font-semibold text-gray-900">
            {formData.first_name?.trim() && formData.last_name?.trim() 
              ? `${formData.first_name} ${formData.last_name}`.trim()
              : 'Prénom et nom requis'
            }
          </p>
        </div>
        {/* Paramètres de sécurité */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-2" />
            Paramètres de sécurité
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Langue
              </label>
              <select
                value={formData.locale || 'fr'}
                onChange={(e) => handleInputChange('locale', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={loading}
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_blocked"
                  checked={formData.is_blocked || false}
                  onChange={(e) => handleInputChange('is_blocked', e.target.checked)}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="is_blocked" className="ml-2 block text-sm text-gray-700">
                  Utilisateur bloqué
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="must_change_password"
                  checked={formData.must_change_password || false}
                  onChange={(e) => handleInputChange('must_change_password', e.target.checked)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="must_change_password" className="ml-2 block text-sm text-gray-700">
                  Forcer le changement de mot de passe
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="twofa_enabled"
                  checked={formData.twofa_enabled || false}
                  onChange={(e) => handleInputChange('twofa_enabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="twofa_enabled" className="ml-2 block text-sm text-gray-700">
                  Authentification à deux facteurs
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Informations système (lecture seule) */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informations système</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div>
              <span className="font-medium">Compte créé le :</span>
              <p className="mt-1">
                {user.created_at ? new Date(user.created_at).toLocaleString('fr-FR') : 'Non disponible'}
              </p>
            </div>
            <div>
              <span className="font-medium">Dernière connexion :</span>
              <p className="mt-1">
                {user.updated_at ? new Date(user.updated_at).toLocaleString('fr-FR') : 'Jamais connecté'}
              </p>
            </div>
            <div>
              <span className="font-medium">Statut actuel :</span>
              <div className="mt-1">
                {user.is_blocked ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircleIcon className="h-3 w-3 mr-1" />
                    Bloqué
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    Actif
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            disabled={loading || isSubmitting}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sauvegarde...
              </div>
            ) : (
              'Sauvegarder les modifications'
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default EditUserModal;
